import axios from 'axios';
import { adapterConfig, API_TIMEOUTS } from '../config';
import { useAuthStore } from '@stores/authStore';
import type { SessionView } from '@/types/session';

export const adapterClient = axios.create({
  baseURL: adapterConfig.baseURL,
  timeout: API_TIMEOUTS.default,
  withCredentials: true,
  headers: { Accept: 'application/json' },
});

const publicAuthPaths = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/passkeys/login/options',
  '/auth/passkeys/login/verify',
]);

adapterClient.interceptors.request.use((config) => {
  // Remove obsolete headers, including ones passed explicitly by unmigrated consumers.
  config.headers.delete('Authorization');
  config.headers.delete('x-user-email');
  const csrf = useAuthStore.getState().session?.csrfToken;
  if (
    csrf &&
    !['get', 'head', 'options'].includes(config.method ?? 'get') &&
    !publicAuthPaths.has(config.url ?? '')
  )
    config.headers.set('X-CSRF-Token', csrf);
  return config;
});

let sessionCheck: Promise<void> | null = null;
adapterClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? '';
      const currentSession = useAuthStore.getState().session;
      if (
        currentSession &&
        url !== '/auth/session' &&
        !publicAuthPaths.has(url) &&
        !url.startsWith('/auth/passkeys/')
      ) {
        sessionCheck ??= adapterClient
          .get<SessionView>('/auth/session')
          .then(({ data }) => {
            if (useAuthStore.getState().session === currentSession)
              useAuthStore.getState().setSession(data);
          })
          .catch((checkError: unknown) => {
            if (
              axios.isAxiosError(checkError) &&
              checkError.response?.status === 401 &&
              useAuthStore.getState().session === currentSession
            )
              useAuthStore.getState().logout();
          })
          .finally(() => {
            sessionCheck = null;
          });
        await sessionCheck;
      }
    }
    // Never replay a mutation or trigger a biometric prompt from an interceptor.
    return Promise.reject(error);
  }
);

export class BackendError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code: string,
    public context: string
  ) {
    super(message);
    this.name = 'BackendError';
  }
}

export function handleApiError(error: unknown, context: string): never {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const data = error.response?.data as
      | { code?: string; message?: string }
      | undefined;
    const messages: Record<string, string> = {
      username_taken:
        'That username is already registered. Log in to continue an unfinished signup.',
      invalid_credentials:
        'The credentials could not be verified. Please try again.',
      csrf_invalid:
        'Your session changed. Reload the page before trying again.',
      origin_not_allowed:
        'This frontend origin is not allowed by the server configuration.',
      invalid_request:
        'Some information is invalid. Check the fields and try again.',
      capacity_exceeded: 'The service is busy. Please try again shortly.',
      dependency_unavailable:
        'A required service is unavailable. Please try again shortly.',
    };
    let message =
      messages[data?.code ?? ''] ??
      data?.message ??
      (status
        ? 'The request failed. Please try again.'
        : 'Cannot reach the server. Check your connection and try again.');
    if (context === 'register' && (!status || status >= 500)) {
      message +=
        ' Your account may already exist. Try logging in with the same username and password to recover.';
    }
    throw new BackendError(
      message,
      status,
      data?.code ?? 'network_error',
      context
    );
  }
  throw error;
}
